/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import { useAppSelector } from '../../../store';
import { searchSettingsTranslation } from '../../actions/util/sysTranslations';
import { messages } from '../../elastic-search/hooks/messages';
import { selectAssistant } from '../../ia/store/assistants/core-selectors';
import { MULTILINGUAL_CONVERSATIONAL_SEARCH_LANGUAGES } from '../../ia/util/multiLingualConvoSearch';
import {
  setCustomServiceSearchSkillDraft,
  setDraftSuccessMessage,
  setDraftNoResultsMessage,
  setDraftConnectivityMessage,
  setCustomServiceDraftIsConversationalSearchEnabled,
  setDraftCitationsTitleValue,
  setDraftMaxCitationsValue,
  setDraftConversationalSearchEnabledDefault,
  setDraftConversationalSearchRetrievalConfidenceThresholdDefault,
  setDraftConversationalSearchRetrievalConfidenceThreshold,
  setDraftConversationalSearchConfidenceThresholdDefault,
  setDraftConversationalSearchConfidenceThreshold,
  setDraftConversationalSearchVerbosityOption,
  setDraftConversationalSearchVerbosityOptionDefault,
  setDraftConversationalSearchResponseConfidenceThresholdDefault,
  setDraftConversationalSearchResponseConfidenceThreshold,
  setDraftCitationsTitleValueDefault,
  setDraftMaxCitationsValueDefault,
  setDraftConversationalSearchMultiTurn,
  setDraftConversationalSearchMultiTurnDefault
} from '../store/reducer';
import {
  selectDraftSuccessMessage,
  selectDraftNoResultsMessage,
  selectDraftConnectivityMessage,
  selectIsDirty,
  selectDraftSkillId,
  selectDraftIsConversationalSearchEnabled,
  selectDraftConversationalSearchRetrievalConfidenceThreshold,
  selectDraftConversationalSearchConfidenceThreshold,
  selectDraftConversationalSearchVerbosityOption,
  selectDraftConversationalSearchResponseConfidenceThreshold,
  selectDraftCitationsTitleValue,
  selectDraftMaxCitationsValue,
  selectDraftConversationalSearchMultiTurn
} from '../store/selectors';

export const useCustomServiceDraft = () => {
  const dispatch = useDispatch();
  const { formatMessage: t } = useIntl();
  const assistant = useAppSelector(selectAssistant);
  // There is currently a store bug that auto-creates all draft and live
  // skills with the language 'xx'. To get the correct language associated
  // with the skill, we must grab from the assistant
  const language = assistant?.language;

  const initializeAutocreatedCustomServiceSkill = skill => {
    const autocreatedSkill = _.cloneDeep(skill);
    let searchSettingsMessages = {
      success: t(messages.successDefault),
      no_result: t(messages.noResultsDefault),
      error: t(messages.connectivityIssueDefault)
    };
    if (MULTILINGUAL_CONVERSATIONAL_SEARCH_LANGUAGES.includes(language)) {
      searchSettingsMessages = searchSettingsTranslation[language].messages;
    }
    autocreatedSkill.language = language || 'en';
    autocreatedSkill.search_settings = {
      client_side_search: {
        filter: '',
        metadata: null
      },
      server_side_search: {
        url: '',
        username: '',
        password: '',
        apikey: '',
        filter: '',
        metadata: null
      },
      messages: searchSettingsMessages
    };
    dispatch(
      setCustomServiceSearchSkillDraft({
        skill: autocreatedSkill,
        isDirty: true
      })
    );
  };

  const isDirty = useSelector(selectIsDirty);
  const skillId = useSelector(selectDraftSkillId);

  const successMessage = useSelector(selectDraftSuccessMessage);
  const setSuccessMessage = value => {
    dispatch(setDraftSuccessMessage(value));
  };

  const noResultsMessage = useSelector(selectDraftNoResultsMessage);

  const setNoResultsMessage = value =>
    dispatch(setDraftNoResultsMessage(value));

  const connectivityMessage = useSelector(selectDraftConnectivityMessage);

  const setConnectivityMessage = value =>
    dispatch(setDraftConnectivityMessage(value));

  const isConversationalSearchEnabled = useSelector(
    selectDraftIsConversationalSearchEnabled
  );

  const conversationalSearchRetrievalConfidenceThreshold = useSelector(
    selectDraftConversationalSearchRetrievalConfidenceThreshold
  );
  const conversationalSearchConfidenceThreshold = useSelector(
    selectDraftConversationalSearchConfidenceThreshold
  );
  const conversationalSearchVerbosityOption = useSelector(
    selectDraftConversationalSearchVerbosityOption
  );
  const conversationalSearchResponseConfidenceThreshold = useSelector(
    selectDraftConversationalSearchResponseConfidenceThreshold
  );

  const setIsConversationalSearchEnabled = value =>
    dispatch(setCustomServiceDraftIsConversationalSearchEnabled(value));

  const setConversationalSearchRetrievalConfidenceThreshold = value => {
    dispatch(setDraftConversationalSearchRetrievalConfidenceThreshold(value));
  };
  const setConversationalSearchConfidenceThreshold = value => {
    dispatch(setDraftConversationalSearchConfidenceThreshold(value));
  };
  const setConversationalSearchVerbosityOption = value => {
    dispatch(setDraftConversationalSearchVerbosityOption(value));
  };
  const setConversationalSearchResponseConfidenceThreshold = value => {
    dispatch(setDraftConversationalSearchResponseConfidenceThreshold(value));
  };

  const citationsTitleValue = useSelector(selectDraftCitationsTitleValue);
  const maxCitationsValue = useSelector(selectDraftMaxCitationsValue);

  const setCitationsTitleValue = value =>
    dispatch(setDraftCitationsTitleValue(value));

  const setMaxCitationsValue = value =>
    dispatch(setDraftMaxCitationsValue(value));

  const setConversationalSearchEnabledDefault = () =>
    dispatch(setDraftConversationalSearchEnabledDefault());

  const setConversationalSearchRetrievalConfidenceThresholdDefault = () =>
    dispatch(setDraftConversationalSearchRetrievalConfidenceThresholdDefault());
  const setConversationalSearchConfidenceThresholdDefault = () =>
    dispatch(setDraftConversationalSearchConfidenceThresholdDefault());
  const setConversationalSearchVerbosityOptionDefault = () =>
    dispatch(setDraftConversationalSearchVerbosityOptionDefault());
  const setConversationalSearchResponseConfidenceThresholdDefault = () =>
    dispatch(setDraftConversationalSearchResponseConfidenceThresholdDefault());

  const setCitationsTitleValueDefault = () =>
    dispatch(
      setDraftCitationsTitleValueDefault(t(messages.citationsTitleDefault))
    );
  const setMaxCitationsValueDefault = () =>
    dispatch(setDraftMaxCitationsValueDefault(-1));

  const setConversationalSearchMultiTurnDefault = () =>
    dispatch(setDraftConversationalSearchMultiTurnDefault());
  const setConversationalSearchMultiTurn = value => {
    dispatch(setDraftConversationalSearchMultiTurn(value));
  };
  const isConversationalSearchMultiTurnEnabled = useSelector(
    selectDraftConversationalSearchMultiTurn
  );

  return {
    initializeAutocreatedCustomServiceSkill,
    isDirty,
    skillId,
    successMessage,
    setSuccessMessage,
    noResultsMessage,
    setNoResultsMessage,
    connectivityMessage,
    setConnectivityMessage,
    isConversationalSearchEnabled,
    conversationalSearchRetrievalConfidenceThreshold,
    conversationalSearchConfidenceThreshold,
    conversationalSearchVerbosityOption,
    conversationalSearchResponseConfidenceThreshold,
    setIsConversationalSearchEnabled,
    setConversationalSearchRetrievalConfidenceThreshold,
    setConversationalSearchConfidenceThreshold,
    setConversationalSearchVerbosityOption,
    setConversationalSearchResponseConfidenceThreshold,
    citationsTitleValue,
    setCitationsTitleValue,
    maxCitationsValue,
    setMaxCitationsValue,
    setConversationalSearchEnabledDefault,
    setConversationalSearchRetrievalConfidenceThresholdDefault,
    setConversationalSearchConfidenceThresholdDefault,
    setConversationalSearchVerbosityOptionDefault,
    setConversationalSearchResponseConfidenceThresholdDefault,
    setCitationsTitleValueDefault,
    setMaxCitationsValueDefault,
    setConversationalSearchMultiTurnDefault,
    setConversationalSearchMultiTurn,
    isConversationalSearchMultiTurnEnabled
  };
};
